import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Drawer } from 'vaul';
import { MarketContextDrawer } from '../ai/MarketContextDrawer';
import { TradingViewChart } from '../chart/TradingViewChart';
import { AlarmSetupSheet } from '../alarms/AlarmSetupSheet';
import { PwaInstallPromo } from '../pwa/PwaInstallPromo';
import { storeJSON } from '../settings/storage';
import { AddToWatchlistSheet } from './AddToWatchlistSheet';
import { AppLaunchScreen } from './AppLaunchScreen';
import { BottomNav } from './BottomNav';
import { ImportLoadingOverlay } from './ImportLoadingOverlay';
import { ImportResultSheet } from './ImportResultSheet';
import { MenuView } from './MenuView';
import { NewsView } from './NewsView';
import { PlaceholderView } from './PlaceholderView';
import { SymbolPicker } from './SymbolPicker';
import { WatchlistOptionsSheet } from './WatchlistOptionsSheet';
import { WatchlistView } from './WatchlistView';
import { activeTabStorageKey, loadStoredActiveTab } from './AppShell.utils';
import type { AppTab, WatchlistViewItem } from './AppShell.types';
import type { WatchlistImportSummary } from '../types/api';
import { useAppData } from './useAppData';
import './app-shell.css';

export function MobileShell() {
  const app = useAppData();
  const [activeTab, setActiveTab] = useState<AppTab>(() => loadStoredActiveTab());
  const [chartMounted, setChartMounted] = useState(false);
  const [watchlistOptionsOpen, setWatchlistOptionsOpen] = useState(false);
  const [openSwipeItemId, setOpenSwipeItemId] = useState<string | null>(null);
  const [createSectionTarget, setCreateSectionTarget] = useState<WatchlistViewItem | 'end' | null>(null);
  const [createSectionName, setCreateSectionName] = useState('');
  const [createWatchlistOpen, setCreateWatchlistOpen] = useState(false);
  const [createWatchlistName, setCreateWatchlistName] = useState('');
  const [renameWatchlistTarget, setRenameWatchlistTarget] = useState<string | null>(null);
  const [renameWatchlistName, setRenameWatchlistName] = useState('');
  const [deleteWatchlistTarget, setDeleteWatchlistTarget] = useState<{ id: string; name: string } | null>(null);
  const [renameSectionTarget, setRenameSectionTarget] = useState<WatchlistViewItem | null>(null);
  const [renameSectionName, setRenameSectionName] = useState('');
  const [marketContextOpen, setMarketContextOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ fileName: string; summary: WatchlistImportSummary; } | null>(null);
  const createSectionInputRef = useRef<HTMLInputElement | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const shouldRenderChart = chartMounted && app.ready;

  function focusCreateSectionInput() {
    requestAnimationFrame(() => createSectionInputRef.current?.focus({ preventScroll: true }));
    for (const delay of [0, 120, 280, 520]) {
      window.setTimeout(() => createSectionInputRef.current?.focus({ preventScroll: true }), delay);
    }
  }

  function openCreateSection(target: WatchlistViewItem | 'end') {
    setCreateSectionName('');
    setCreateSectionTarget(target);
  }

  function closeCreateSection() {
    setCreateSectionTarget(null);
    setCreateSectionName('');
  }

  function submitCreateSection() {
    const name = createSectionName.trim();
    if (!name || !createSectionTarget) return;
    if (createSectionTarget === 'end') void app.addSectionToWatchlist(name);
    else void app.addSectionAboveWatchlistItem(createSectionTarget, name);
    closeCreateSection();
  }

  function closeCreateWatchlist() {
    setCreateWatchlistOpen(false);
    setCreateWatchlistName('');
  }

  function openRenameWatchlist(watchlist: { id: string; name: string }) {
    setRenameWatchlistTarget(watchlist.id);
    setRenameWatchlistName(watchlist.name);
  }

  function closeRenameWatchlist() {
    setRenameWatchlistTarget(null);
    setRenameWatchlistName('');
  }

  function submitRenameWatchlist() {
    if (!renameWatchlistTarget) return;
    const watchlist = app.watchlists.find((item) => item.id === renameWatchlistTarget);
    const name = renameWatchlistName.trim();
    if (!watchlist || !name) return;
    void app.renameWatchlistWithName(watchlist, name);
    closeRenameWatchlist();
  }

  function openDeleteWatchlist(watchlist: { id: string; name: string }) {
    setDeleteWatchlistTarget(watchlist);
  }

  function closeDeleteWatchlist() {
    setDeleteWatchlistTarget(null);
  }

  function submitDeleteWatchlist() {
    if (!deleteWatchlistTarget) return;
    const watchlist = app.watchlists.find((item) => item.id === deleteWatchlistTarget.id);
    if (!watchlist) return;
    void app.deleteWatchlistDirect(watchlist);
    closeDeleteWatchlist();
  }

  function openRenameSection(item: WatchlistViewItem) {
    setRenameSectionTarget(item);
    setRenameSectionName(item.name ?? '');
  }

  function closeRenameSection() {
    setRenameSectionTarget(null);
    setRenameSectionName('');
  }

  function submitRenameSection() {
    if (!renameSectionTarget) return;
    const name = renameSectionName.trim();
    if (!name) return;
    void app.renameSectionWithName(renameSectionTarget, name);
    closeRenameSection();
  }

  useEffect(() => {
	function handleShortcut(event: KeyboardEvent) {
	  const key = event.key.toLowerCase();
	  if ((key !== 'w' && key !== 'b') || !event.altKey || event.metaKey || event.ctrlKey) return;
	  const target = event.target as HTMLElement | null;
	  if (target && (target.closest('input, textarea, select, [contenteditable="true"]') || target.isContentEditable)) return;
	  event.preventDefault();
	  event.stopPropagation();
	  event.stopImmediatePropagation();
	  void (key === 'w' ? app.toggleCurrentChartSymbolInWatchlist() : app.toggleCurrentChartSymbolFlag());
	}

	window.addEventListener('keydown', handleShortcut, true);
	return () => window.removeEventListener('keydown', handleShortcut, true);
  }, [app]);

  function submitCreateWatchlist() {
    const name = createWatchlistName.trim();
    if (!name) return;
    void app.createNewWatchlist(name);
    closeCreateWatchlist();
  }

  function openImportPicker() {
    importInputRef.current?.click();
  }

  async function handleImportChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const result = await app.importFileToWatchlist(file);
      if (result) setImportResult({ fileName: file.name, summary: result.summary });
    } finally {
      setImporting(false);
    }
  }

  useEffect(() => {
    if (app.checkingSession || app.user) return;
    setActiveTab('watchlist');
  }, [app.checkingSession, app.user]);

  useEffect(() => {
    if (activeTab === 'chart') setChartMounted(true);
  }, [activeTab]);

  useEffect(() => {
    storeJSON(activeTabStorageKey, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!app.ready) return;

    const params = new URLSearchParams(window.location.search);
    const symbol = params.get('symbol')?.trim().toUpperCase();
    if (!symbol) return;

    app.setSelectedSymbol(symbol);
    setActiveTab('chart');
    params.delete('symbol');
    params.delete('tab');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [app.ready]);

  useEffect(() => {
    if (!createSectionTarget) return;
    focusCreateSectionInput();
  }, [createSectionTarget]);

  return (
    <main className="app-shell">
      <header className={activeTab === 'watchlist' ? 'mobile-topbar' : 'mobile-topbar mobile-topbar-empty'}>
        {activeTab === 'watchlist' && (
          <>
            <button type="button" className="icon-button" aria-label="Watchlist options" onClick={() => setWatchlistOptionsOpen(true)}>...</button>
            <button type="button" className="icon-button" aria-label="Add symbol" onClick={() => app.setSymbolPickerOpen(true)}><Plus size={34} strokeWidth={1.7} /></button>
          </>
        )}
      </header>

      <section className="app-content">
        <input ref={importInputRef} type="file" accept=".txt,text/plain" hidden onChange={handleImportChange} />
        {activeTab === 'watchlist' && (
          <WatchlistView
            watchlists={app.watchlists}
            activeWatchlistId={app.activeWatchlist?.id ?? null}
            items={app.activeWatchlistItems}
            selectedSymbol={app.chartSymbol}
            sort={app.watchlistSort}
            loading={app.watchlistLoading || (app.quotesLoading && !app.hasWatchlistQuotes)}
            quotes={app.quotes}
            draggingItemId={app.draggingItemId}
            dragOverItemId={app.dragOverItemId}
            draggingWatchlistId={app.draggingWatchlistId}
            dragOverWatchlistId={app.dragOverWatchlistId}
            openSwipeItemId={openSwipeItemId}
            error={app.watchlistError}
            canManageWatchlists={app.canManageWatchlists}
            canUseAlarms={app.canUseAlarms}
            onWatchlistChange={app.setActiveWatchlistId}
            onCreateWatchlist={() => setCreateWatchlistOpen(true)}
            onRenameWatchlist={openRenameWatchlist}
            onDeleteWatchlist={openDeleteWatchlist}
            onSelectSymbol={(symbol) => {
              app.setSelectedSymbol(symbol.name);
              setActiveTab('chart');
            }}
            onRemoveSymbol={(symbol) => {
              setOpenSwipeItemId(null);
              void app.removeSymbolFromWatchlist(symbol);
            }}
            onCreateSectionAbove={openCreateSection}
            onRemoveItem={(item) => {
              setOpenSwipeItemId(null);
              void app.removeWatchlistItem(item);
            }}
            onRenameSection={(item) => {
              setOpenSwipeItemId(null);
              openRenameSection(item);
            }}
            onAlarmSymbol={(symbol) => {
              setOpenSwipeItemId(null);
              if (app.canUseAlarms) app.setAlarmSymbol(symbol);
            }}
            onSetItemFlag={(item, flagColor) => {
              setOpenSwipeItemId(null);
              void app.setWatchlistItemFlagColor(item, flagColor);
            }}
            onReorderStart={(itemId, currentOrder) => {
              setOpenSwipeItemId(null);
              app.startReorder(itemId, currentOrder);
            }}
            onReorderPreview={app.previewReorder}
            onReorderEnd={() => void app.finishReorder()}
            onReorderCancel={app.cancelReorder}
            onWatchlistReorderStart={app.startWatchlistReorder}
            onWatchlistReorderPreview={app.previewWatchlistReorder}
            onWatchlistReorderEnd={() => void app.finishWatchlistReorder()}
            onWatchlistReorderCancel={app.cancelWatchlistReorder}
            onOpenSwipe={setOpenSwipeItemId}
            onCloseSwipe={() => setOpenSwipeItemId(null)}
          />
        )}

        <section className={activeTab === 'chart' ? 'chart-frame' : 'chart-frame chart-frame-hidden'}>
          {shouldRenderChart ? (
            <TradingViewChart
              key={app.chartSymbol}
              ref={app.chartRef}
              datafeed={app.datafeed}
              symbol={app.chartSymbol}
              interval={app.chartInterval}
              layout={app.layout}
              settingsAdapter={app.settingsAdapter}
              activeWatchlistSymbols={app.activeWatchlistSymbols}
              enableCustomIndicators={Boolean(app.user)}
              enableNineWeekInterval={app.canUseNineWeekInterval}
              onAutoSave={app.persistLayout}
              onFlush={app.flushSettings}
              onSymbolSelectorOpen={() => app.setSymbolPickerOpen(true)}
              onSymbolChange={app.setSelectedSymbol}
              onIntervalChange={app.setSelectedInterval}
              watchlistSymbolNames={app.watchlistSymbolNames}
              flaggedWatchlistSymbolNames={app.flaggedWatchlistSymbolNames}
              flaggedWatchlistSymbolColors={app.flaggedWatchlistSymbolColors}
              alarms={app.canUseAlarms ? app.chartAlarms : []}
              layoutMode="mobile"
              onDeleteAlarm={app.canUseAlarms ? (id) => void app.deleteChartAlarm(id) : undefined}
              onPlusClick={app.canUseAlarms ? (params) => void app.openAlarmForSymbolName(params.symbol, params.price) : undefined}
              onMarketContextOpen={() => setMarketContextOpen(true)}
              onAddToWatchlist={app.addToWatchlistFromChart}
              onRemoveFromWatchlist={app.removeFromWatchlistFromChart}
              onToggleWatchlistShortcut={app.toggleCurrentChartSymbolInWatchlist}
              onToggleFlagShortcut={app.toggleCurrentChartSymbolFlag}
              onSetFlagColor={app.setCurrentChartSymbolFlag}
            />
          ) : activeTab === 'chart' ? (
            <AppLaunchScreen />
          ) : null}
        </section>

        <NewsView active={activeTab === 'news'} />
        {activeTab === 'explore' && <PlaceholderView title="Explore" description="Market discovery placeholder for day 1." />}
        {activeTab === 'community' && <PlaceholderView title="Community" description="Community feed placeholder for day 1." />}
        {activeTab === 'menu' && (
          <MenuView
            signedIn={Boolean(app.user)}
            isAdmin={Boolean(app.user?.is_admin)}
            userLabel={app.user?.username || app.user?.email || app.user?.id || 'Guest'}
            cloudSyncEnabled={app.cloudSyncEnabled}
            syncing={app.syncing}
            syncDisabled={!app.ready}
            onCloudSyncChange={app.setCloudSyncEnabled}
            onSyncNow={() => void app.syncNow()}
            onSignOut={() => void app.signOut()}
          />
        )}
      </section>

      <BottomNav activeTab={activeTab} onChange={setActiveTab} />
      <SymbolPicker
        open={app.symbolPickerOpen}
        symbols={app.symbolPickerSymbols.length > 0 || app.symbolSearch.trim() ? app.symbolPickerSymbols : app.symbols}
        watchlists={app.watchlists}
        query={app.symbolSearch}
        category={app.symbolPickerCategory}
        loading={app.symbolPickerLoading}
        onCategoryChange={app.setSymbolPickerCategory}
        onQueryChange={app.setSymbolSearch}
        onOpenChange={app.setSymbolPickerOpen}
        onSelect={(symbol) => {
          app.setSelectedSymbol(symbol.name);
          setActiveTab('chart');
          app.setSymbolPickerOpen(false);
        }}
        onAdd={app.requestAddSymbolToWatchlist}
      />
      <AddToWatchlistSheet
        symbol={app.addToWatchlistSymbol}
        watchlists={app.watchlists}
        addingWatchlistId={app.addingWatchlistId}
        onAdd={(watchlist) => void app.addPendingSymbolToWatchlist(watchlist)}
        onOpenChange={(open) => {
          if (!open && !app.addingWatchlistId) app.setAddToWatchlistSymbol(null);
        }}
      />
      <WatchlistOptionsSheet
        open={watchlistOptionsOpen}
        sort={app.watchlistSort}
        canManageWatchlists={app.canManageWatchlists}
        onSortChange={app.setWatchlistSort}
        onAddSymbol={() => {
          setWatchlistOptionsOpen(false);
          app.setSymbolPickerOpen(true);
        }}
        onImportWatchlist={() => {
          setWatchlistOptionsOpen(false);
          openImportPicker();
        }}
        onAddSection={() => {
          setWatchlistOptionsOpen(false);
          openCreateSection('end');
        }}
        onRenameWatchlist={() => {
          setWatchlistOptionsOpen(false);
          if (app.activeWatchlist) openRenameWatchlist(app.activeWatchlist);
        }}
        onDeleteWatchlist={() => {
          setWatchlistOptionsOpen(false);
          if (app.activeWatchlist) openDeleteWatchlist(app.activeWatchlist);
        }}
        canDeleteWatchlist={app.watchlists.length > 1}
        onOpenChange={setWatchlistOptionsOpen}
      />
      <Drawer.Root open={Boolean(createSectionTarget)} onOpenChange={(open) => {
        if (!open) closeCreateSection();
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content
            className="drawer-content create-section-sheet"
            aria-describedby={undefined}
            onAnimationEnd={focusCreateSectionInput}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              focusCreateSectionInput();
            }}
          >
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>Create section</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close create section"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <form className="create-section-form" onSubmit={(event) => {
              event.preventDefault();
              submitCreateSection();
            }}>
              <input
                ref={createSectionInputRef}
                autoFocus
                value={createSectionName}
                placeholder="Section name"
                onChange={(event) => setCreateSectionName(event.target.value)}
              />
              <button type="submit" className="sheet-action-button" disabled={!createSectionName.trim()}>Create</button>
            </form>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      <Drawer.Root open={createWatchlistOpen} onOpenChange={(open) => {
        if (!open) closeCreateWatchlist();
        else setCreateWatchlistOpen(true);
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content create-section-sheet" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>Create watchlist</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close create watchlist"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <form className="create-section-form" onSubmit={(event) => {
              event.preventDefault();
              submitCreateWatchlist();
            }}>
              <input
                autoFocus
                value={createWatchlistName}
                placeholder="Watchlist name"
                onChange={(event) => setCreateWatchlistName(event.target.value)}
              />
              <button type="submit" className="sheet-action-button" disabled={!createWatchlistName.trim()}>Create</button>
            </form>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      <Drawer.Root open={Boolean(renameWatchlistTarget)} onOpenChange={(open) => {
        if (!open) closeRenameWatchlist();
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content create-section-sheet" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>Rename watchlist</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close rename watchlist"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <form className="create-section-form" onSubmit={(event) => {
              event.preventDefault();
              submitRenameWatchlist();
            }}>
              <input autoFocus value={renameWatchlistName} placeholder="Watchlist name" onChange={(event) => setRenameWatchlistName(event.target.value)} />
              <button type="submit" className="sheet-action-button" disabled={!renameWatchlistName.trim()}>Save</button>
            </form>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      <Drawer.Root open={Boolean(deleteWatchlistTarget)} onOpenChange={(open) => {
        if (!open) closeDeleteWatchlist();
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content create-section-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>Delete watchlist</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close delete watchlist"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <div className="sheet-confirm-copy">Delete {deleteWatchlistTarget?.name ?? 'watchlist'}?</div>
            <div className="sheet-confirm-actions">
              <button type="button" className="sheet-action-button" onClick={closeDeleteWatchlist}>Cancel</button>
              <button type="button" className="sheet-action-button sheet-action-button-danger" onClick={submitDeleteWatchlist}>Delete</button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      <Drawer.Root open={Boolean(renameSectionTarget)} onOpenChange={(open) => {
        if (!open) closeRenameSection();
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content create-section-sheet" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>Rename section</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close rename section"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <form className="create-section-form" onSubmit={(event) => {
              event.preventDefault();
              submitRenameSection();
            }}>
              <input autoFocus value={renameSectionName} placeholder="Section name" onChange={(event) => setRenameSectionName(event.target.value)} />
              <button type="submit" className="sheet-action-button" disabled={!renameSectionName.trim()}>Save</button>
            </form>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      {app.canUseAlarms ? (
        <AlarmSetupSheet
          open={Boolean(app.alarmSymbol)}
          symbol={app.alarmSymbol}
          initialPrice={app.alarmInitialPrice ?? (app.alarmSymbol ? app.quotes[app.alarmSymbol.name]?.price : undefined)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) app.closeAlarmSetup();
          }}
        />
      ) : null}
      <PwaInstallPromo />
      <MarketContextDrawer open={marketContextOpen} symbol={app.chartSymbol} interval={app.chartInterval} onOpenChange={setMarketContextOpen} />
      <ImportLoadingOverlay open={importing} />
      <ImportResultSheet
        open={Boolean(importResult)}
        fileName={importResult?.fileName ?? ''}
        summary={importResult?.summary ?? null}
        onOpenChange={(open) => {
          if (!open) setImportResult(null);
        }}
      />
    </main>
  );
}
